import React, { useMemo, useState } from "react";

import {
  faCircleExclamation,
  faCircleStop,
  faCopy,
  faEye,
  faLink,
  faPage,
  faPlay,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { isAfter } from "date-fns";
import { twJoin } from "tailwind-merge";
import { useDebouncedCallback } from "use-debounce";

import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import {
  TableComp as Table,
  Cell as TableCell,
  Header as TableHeader,
} from "src/base-components/Table";
import { EmptyState } from "src/design-system/EmptyState";
import { Icon, IconButton } from "src/design-system/Icon";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { ExcludesFalse } from "src/flow/types";
import { buildTestRunParamValue } from "src/flowContainer/TestRunsSidePane/hooks";
import { JobRunStatus } from "src/flowContainer/TestRunsSidePane/JobRunStatus";
import { JobRun, JobRunStatusType } from "src/jobs/types";
import { usePowertools } from "src/powertools/hooks";
import { useFlowContext } from "src/router/routerContextHooks";
import { LeftPaneOptions } from "src/router/SearchParams";
import { getBaseUrl, getUrlToAuthoringPage } from "src/router/urls";
import { copyTextToClipboard } from "src/utils/clipboard";
import { formatDate } from "src/utils/datetime";

const columnTextClassName = "text-gray-800 font-inter-medium-13px";
const datasetColumnSize = 118;
const timestampColumnSize = 175;
const resultsColumnSize = 200;
const helper = createColumnHelper<JobRun>();
const UNKNOWN_DATASET_NAME = "Unknown Dataset";
const DATE_FORMAT = "MMM d, yyyy 'at' h:mm a";

const canLoadTestRun = (jobRun: JobRun) => {
  return [
    JobRunStatusType.Completed,
    JobRunStatusType.Running,
    JobRunStatusType.Writing,
  ].includes(jobRun.status.type);
};

const renderActions = ({
  id,
  testRunUrl,
  canLoad,
  isOutdated,
  isRunning,
  stopTestRun,
  loadTestRun,
}: {
  id: string | null;
  isOutdated: boolean;
  isRunning: boolean;
  canLoad: boolean;
  testRunUrl: string;
  stopTestRun: () => void;
  loadTestRun: () => void;
}) => {
  const elements = [
    canLoad && {
      action: loadTestRun,
      icon: faEye,
      key: "See results",
    },
    canLoad && {
      key: "Copy test run url",
      icon: faLink,
      action: async () => {
        await copyTextToClipboard(testRunUrl ?? "");
        toastActions.success({ title: "Test run url copied to clipboard" });
      },
    },
    Boolean(id) && {
      key: "Copy test run id",
      icon: faCopy,
      action: async () => {
        await copyTextToClipboard(id ?? "");
        toastActions.success({ title: "Test run id copied to clipboard" });
      },
    },
  ].filter(Boolean as unknown as ExcludesFalse);

  const actionsMenu = (
    <EllipsisOptionsDropdown
      elements={elements}
      dataLoc="test-run-row-buttons-dropdown"
    />
  );

  const actions: React.ReactNode[] = [
    !isRunning && canLoad && isOutdated && (
      <Tooltip
        key="incompatible-test-run"
        title="Outdated test run"
        placement="top"
        body="This decision flow was changed after this test run. You might see incomplete results."
        asChild
      >
        <Icon icon={faCircleExclamation} size="xs" variant="warning" />
      </Tooltip>
    ),

    isRunning && (
      <IconButton
        key="stop-test-run"
        icon={faCircleStop}
        onClick={(e) => {
          e.stopPropagation();
          stopTestRun();
        }}
        size="xs"
      />
    ),

    elements.length > 0 && actionsMenu,
  ];

  const filteredActions = actions.filter(Boolean);

  if (filteredActions.length === 1) {
    filteredActions.unshift(<div key="spacer" className="w-7" />);
  }

  return filteredActions;
};

const getColumns = ({
  orgId,
  workspaceId,
  flowId,
  includeCopyId = false,
}: {
  orgId: string;
  workspaceId: string;
  flowId: string;
  includeCopyId: boolean;
}) => [
  helper.display({
    id: "dataset",
    header: (info) => (
      <TableHeader dataLoc="test-run-dataset-header" info={info}>
        <span className={twJoin("px-4", columnTextClassName)}>Test data</span>
      </TableHeader>
    ),
    cell: (info) => {
      const actions = info.table.options.meta?.actions;
      const datasetName =
        actions?.getDatasetNameBySourceId(info.row.original.source_id) ||
        UNKNOWN_DATASET_NAME;
      return (
        <TableCell className="px-3.5" info={info}>
          <div className="flex h-full w-full items-center">
            <div className="flex items-center overflow-hidden">
              <Icon icon={faPage} size="xs" />

              <div className="min-w-0 flex-1 truncate font-inter-medium-12px text-gray-700">
                {datasetName}
              </div>
            </div>
          </div>
        </TableCell>
      );
    },
    size: datasetColumnSize,
    minSize: datasetColumnSize,
    maxSize: datasetColumnSize,
  }),
  helper.display({
    id: "timestamp",
    header: (info) => (
      <TableHeader dataLoc="test-run-timestamp-header" info={info}>
        <span className={twJoin("px-4", columnTextClassName)}>Timestamp</span>
      </TableHeader>
    ),
    cell: (info) => {
      return (
        <TableCell className="px-3.5" info={info}>
          <div className="flex h-full w-full items-center">
            <div className="flex items-center overflow-hidden">
              <div className="min-w-0 flex-1 truncate font-inter-medium-12px text-gray-700">
                {formatDate(info.row.original.created_at, DATE_FORMAT)}
              </div>
            </div>
          </div>
        </TableCell>
      );
    },
    size: timestampColumnSize,
    minSize: timestampColumnSize,
    maxSize: timestampColumnSize,
  }),
  helper.display({
    id: "results",
    header: (info) => (
      <TableHeader dataLoc="results-header" info={info}>
        <span className={columnTextClassName}>Results</span>
      </TableHeader>
    ),
    cell: (info) => {
      const actions = info.table.options.meta?.actions;
      const isHovered = actions?.shouldShowDetails(info.row.original.id);
      const isLoaded = Boolean(actions?.isLoaded(info.row.original.id));
      const datasetName =
        actions?.getDatasetNameBySourceId(info.row.original.source_id) ??
        UNKNOWN_DATASET_NAME;
      const testRunUrl =
        getBaseUrl() +
        getUrlToAuthoringPage(
          orgId,
          workspaceId,
          flowId,
          info.row.original.flow_version_id ?? "",
          {
            testRunId: buildTestRunParamValue(info.row.original),
            leftPane: LeftPaneOptions.TestRuns,
          },
        );

      return (
        <TableCell
          className={twJoin(
            "border-r-6",
            isLoaded ? "border-r-indigo-400" : "border-r-transparent",
          )}
          info={info}
        >
          <div className="flex h-full w-full items-center justify-between gap-x-2">
            <div className="w-36">
              <JobRunStatus
                jobRun={info.row.original}
                datasetRowCount={
                  actions?.getDatasetRowCountBySourceId(
                    info.row.original.source_id,
                  ) ?? undefined
                }
                footer={getFooter({
                  username:
                    actions?.getUsernameById(
                      "triggered_by" in info.row.original.trigger
                        ? info.row.original.trigger.triggered_by
                        : null,
                    ) ?? null,
                  createdAt: info.row.original.created_at,
                  datasetName,
                })}
                showDetails={
                  actions?.shouldShowDetails(info.row.original.id) ?? false
                }
              />
            </div>
            <div
              className={twJoin(
                "ml-auto flex items-center gap-x-2 group-hover:opacity-100",
                isLoaded || isHovered ? "opacity-100" : "opacity-0",
              )}
              data-loc="test-run-row-buttons-container"
            >
              {renderActions({
                id: includeCopyId ? info.row.original.id : null,
                testRunUrl,
                isOutdated: Boolean(
                  actions?.isOutdatedTestRun(info.row.original),
                ),
                isRunning: isTestRunRunning(info.row.original),
                canLoad: canLoadTestRun(info.row.original),
                stopTestRun: () => {
                  actions?.stopTestRun(info.row.original);
                },
                loadTestRun: () => {
                  actions?.loadTestRun(info.row.original);
                },
              })}
            </div>
          </div>
        </TableCell>
      );
    },
    size: resultsColumnSize,
    minSize: resultsColumnSize,
    maxSize: resultsColumnSize,
  }),
];

export const TestRunsTable: React.FC<{
  lastUpdatedAt: string;
  loadedTestRunId: string | null;
  jobRuns: JobRun[];
  datasetNamesBySourceId: Record<string, string>;
  datasetRowCountsBySourceId: Record<string, number>;
  usernameById: Record<string, string | null>;
  isLoading: boolean;
  isEmpty: boolean;
  onStop: (jobRun: JobRun) => void;
  onLoad: (jobRun: JobRun) => void;
}> = ({
  datasetNamesBySourceId,
  datasetRowCountsBySourceId,
  loadedTestRunId,
  usernameById,
  isLoading,
  isEmpty,
  jobRuns,
  lastUpdatedAt,
  onStop,
  onLoad,
}) => {
  const { orgId, workspace, flow } = useFlowContext();
  const [showDetailsFor, setShowDetailsFor] = useState<string | null>(null);
  const isPowertoolsEnabled = usePowertools();
  const columns = useMemo(
    () =>
      getColumns({
        orgId,
        workspaceId: workspace.id,
        flowId: flow.id,
        includeCopyId: isPowertoolsEnabled,
      }),
    [workspace.id, flow.id, orgId, isPowertoolsEnabled],
  );

  const handleToggleShowDetails = useDebouncedCallback((testRunId: string) => {
    if (showDetailsFor === testRunId) {
      setShowDetailsFor(null);
    } else {
      setShowDetailsFor(testRunId);
    }
  }, 350);

  if (isLoading || !isEmpty) {
    return (
      <div className="bg-white">
        <Table
          columns={columns}
          data={jobRuns}
          dataLoc="test-runs-table"
          frameClassName="h-auto border-gray-100 border-b"
          isLoading={isLoading}
          noScroll={true}
          rowClassName="group hover:bg-gray-50"
          headerPropsGetter={() => ({
            onClick: () => {
              handleToggleShowDetails.cancel();
              setShowDetailsFor(null);
            },
          })}
          rowPropsGetter={(row) => ({
            "data-loc": row.original.id,
            onClick: () => canLoadTestRun(row.original) && onLoad(row.original),
            onMouseEnter: () => handleToggleShowDetails(row.original.id),
            onMouseLeave: () => {
              handleToggleShowDetails.cancel();
              setShowDetailsFor(null);
            },
            classNameOverrides: twJoin(
              canLoadTestRun(row.original) && "cursor-pointer",
              showDetailsFor === row.original.id && "bg-gray-50",
            ),
          })}
          actions={{
            getDatasetNameBySourceId: (sourceId: string) => {
              return datasetNamesBySourceId[sourceId];
            },
            getDatasetRowCountBySourceId: (sourceId: string) => {
              return datasetRowCountsBySourceId[sourceId];
            },
            getUsernameById: (userId: string) => {
              return usernameById[userId];
            },
            isOutdatedTestRun: (testRun: JobRun) => {
              return isAfter(lastUpdatedAt, testRun.created_at);
            },
            stopTestRun: onStop,
            loadTestRun: onLoad,
            isLoaded: (testRunId: string) => {
              return testRunId === loadedTestRunId;
            },
            shouldShowDetails: (testRunId: string) => {
              return showDetailsFor === testRunId;
            },
            toggleShowDetails: handleToggleShowDetails,
          }}
        />
      </div>
    );
  }

  return (
    <EmptyState
      description="Create your first test run"
      headline="No test runs available"
      icon={faPlay}
    />
  );
};

const isTestRunRunning = (jobRun: JobRun) => {
  return jobRun.status.type === JobRunStatusType.Running;
};

type GetFooterArgs = {
  username: string | null;
  createdAt: string;
  datasetName: string;
};

const getFooter = ({ username, createdAt, datasetName }: GetFooterArgs) => {
  if (username) {
    return (
      <div className="font-inter-normal-12px text-gray-600">
        Test run with the test data file{" "}
        <b className="font-inter-medium-12px text-gray-800">{datasetName}</b>{" "}
        started by{" "}
        <b className="font-inter-medium-12px text-gray-800">{username}</b> on{" "}
        <b className="font-inter-medium-12px text-gray-800">
          {formatDate(createdAt, DATE_FORMAT)}
        </b>
      </div>
    );
  }

  return (
    <p className="font-inter-normal-12px text-gray-600">
      Test run with the test data file {datasetName} started on{" "}
      {formatDate(createdAt, DATE_FORMAT)}
    </p>
  );
};
